import { format, isValid, parse, startOfDay } from 'date-fns';

import DATE_FORMAT from 'config/constants/DATE_FORMAT';
import processing_activities_messages from 'messages/processing_activities_messages';
import validation_messages from 'messages/validation_messages';

export const ROPA_REVIEW_DEADLINE_MAX = '2999-12-31';

export type RopaReviewDeadlineError = 'required' | 'invalid_format' | 'not_future' | 'after_max';

const EXACT_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const parseRopaReviewDeadlineDate = (value: string): Date | null => {
  if (!EXACT_DATE_PATTERN.test(value)) return null;

  const parsed = parse(value, DATE_FORMAT.defaultDate, new Date());
  if (!isValid(parsed)) return null;
  if (format(parsed, DATE_FORMAT.defaultDate) !== value) return null;

  return startOfDay(parsed);
};

export const getRopaReviewDeadlineBounds = (now = new Date()) => {
  const today = startOfDay(now);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const max = parseRopaReviewDeadlineDate(ROPA_REVIEW_DEADLINE_MAX);

  if (!max) {
    throw new Error(`Invalid ROPA review deadline maximum: ${ROPA_REVIEW_DEADLINE_MAX}`);
  }

  return { today, tomorrow, max };
};

export const validateRopaReviewDeadline = (value: string, now = new Date()): RopaReviewDeadlineError | null => {
  const trimmed = value.trim();
  if (!trimmed) return 'required';

  const parsed = parseRopaReviewDeadlineDate(trimmed);
  if (!parsed) return 'invalid_format';

  const { tomorrow, max } = getRopaReviewDeadlineBounds(now);
  if (parsed < tomorrow) return 'not_future';
  if (parsed > max) return 'after_max';

  return null;
};

const deadlineErrorMessages = {
  required: validation_messages.required,
  invalid_format: processing_activities_messages.asset_ropa.modals.start_review.deadline_invalid,
  not_future: processing_activities_messages.asset_ropa.modals.start_review.deadline_future,
  after_max: processing_activities_messages.asset_ropa.modals.start_review.deadline_max,
};

export const getRopaReviewDeadlineErrorMessage = (
  error: RopaReviewDeadlineError | null,
  t: (message: string | string[]) => string,
): string => (error ? t(deadlineErrorMessages[error]) : '');
